//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import moment from 'moment-timezone';
import theme from './Kronos.scss';
import Kronos from '@locus-taxy/react-kronos';
import { compose } from 'recompose';
import { translate } from 'react-i18next';
import {
    InputCaretIcon,
    IconTypes
} from 'common/components/core/icon/InputCaretIcon';
import { SizeEnum } from 'common/enums/UIEnums';
import { ClearButton } from 'common/components/core/button';
import DateTimeUtils, {
    DateStyle,
    TimeStyle
} from 'common/utils/DateTimeUtils';

import type { TFunction } from 'react-i18next';
import warning from 'common/utils/warning';

type Props = {
    theme: Object,
    value: string,
    min?: number | moment,
    max?: number | moment,
    closeOnBlur?: boolean,
    label?: string,
    showDate?: boolean,
    showTime?: boolean,
    onFocus?: () => void,
    onChange: (date: moment) => void,
    dateFormat?: string,
    defaultLevel?: string,
    dateVisible?: boolean,
    controlDateVisibility?: boolean,
    timeVisible?: boolean,
    controlTimeVisibility?: boolean,
    controlVisibility?: boolean,
    testId?: string,
    t: TFunction,
    dateLabel?: string,
    timeLabel?: string,
    showDateTimeSeparator: boolean,
    datePlaceholder?: string,
    timePlaceholder?: string,
    ErrorIcon: React.Component<$FixMe>,
    onClearInput: () => void,
    isClearable: boolean,
    portal?: boolean
};

class DateTimePicker extends React.PureComponent<Props> {
    _isValueAsStringAlreadyReported: boolean;
    static defaultProps = {
        showDate: true,
        showTime: true,
        ErrorIcon: InputCaretIcon,
        size: SizeEnum.Default,
        showDateTimeSeparator: false,
        isClearable: false,
        onClearInput: () => {}
    };

    getDatePickerRef = ref => {
        const { getDatePickerRef, defaultLevel } = this.props;
        if (defaultLevel && ref) {
            ref.setState({ level: defaultLevel }); // #HACK: to show on first click
        }
        getDatePickerRef && getDatePickerRef(ref);
    };

    getTimePickerRef = ref => {
        const { getTimePickerRef, defaultLevel } = this.props;
        if (defaultLevel && ref) {
            ref.setState({ level: defaultLevel });
        }
        getTimePickerRef && getTimePickerRef(ref); // #HACK: to show years by default
    };

    render() {
        const {
            theme,
            value,
            min,
            max,
            closeOnBlur,
            label,
            showDate,
            showTime,
            onFocus,
            onChange,
            dateFormat,
            dateVisible,
            controlDateVisibility,
            timeVisible,
            controlTimeVisibility,
            children,
            error,
            info,
            className,
            shouldTriggerOnChangeForDateTimeOutsideRange,
            fullWidth,
            size,
            testId,
            t,
            dateLabel,
            timeLabel,
            showDateTimeSeparator,
            datePlaceholder,
            timePlaceholder,
            ErrorIcon,
            onClearInput,
            isClearable,
            portal,
            ...others
        } = this.props;
        const sizeClass = SizeEnum.getSizeClass({ theme, size });
        const _dateFormat =
            dateFormat ||
            DateTimeUtils.getFormat({
                dateStyle: DateStyle.SHORT
            });
        const timeFormat = DateTimeUtils.getFormat({
            timeStyle: TimeStyle.SHORT
        });

        const shouldReportStringValue =
            !this._isValueAsStringAlreadyReported &&
            value &&
            typeof value === 'string';

        if (shouldReportStringValue) {
            const reportMessage =
                // eslint-disable-next-line max-len
                'DateTimePicker should not receive value as string. This is to support localisation. Report this to platform';
            warning(false, reportMessage);
            this._isValueAsStringAlreadyReported = true;
        }

        return (
            <div
                className={classnames(theme.dateTimeWrap, className, {
                    [theme.fullWidth]: fullWidth,
                    [sizeClass]: !!sizeClass,
                    [theme.error]: error
                })}
            >
                {label ? <label className={theme.label}>{label}</label> : null}
                {showDate ? (
                    <>
                        <div className={theme.datePickerWithLabel}>
                            {dateLabel && (
                                <div className={theme.label}>{dateLabel}</div>
                            )}
                            <Kronos
                                {...others}
                                inputId={`${testId}Date`}
                                date={value}
                                min={min}
                                max={max}
                                theme={theme}
                                preventClickOnDateTimeOutsideRange
                                shouldTriggerOnChangeForDateTimeOutsideRange={
                                    shouldTriggerOnChangeForDateTimeOutsideRange
                                }
                                onChangeDateTime={onChange}
                                format={_dateFormat}
                                className={classnames(theme.date, {
                                    [theme.noTime]: !showTime
                                })}
                                closeOnBlur={closeOnBlur}
                                onFocus={onFocus}
                                visible={dateVisible}
                                controlVisibility={controlDateVisibility}
                                options={{
                                    format: {
                                        today: t('today')
                                    }
                                }}
                                placeholder={datePlaceholder}
                                ref={this.getDatePickerRef}
                                portal={portal}
                            />
                        </div>

                        {isClearable && value && (
                            <ClearButton theme={theme} onClick={onClearInput} />
                        )}
                    </>
                ) : null}
                {showDateTimeSeparator && showDate && showTime && (
                    <div className={theme.separator} />
                )}
                {showTime ? (
                    <>
                        <div className={theme.datePickerWithLabel}>
                            {timeLabel && (
                                <div className={theme.label}>{timeLabel}</div>
                            )}
                            <Kronos
                                {...others}
                                inputId={`${testId}Time`}
                                time={value}
                                min={min}
                                max={max}
                                theme={theme}
                                preventClickOnDateTimeOutsideRange
                                shouldTriggerOnChangeForDateTimeOutsideRange={
                                    shouldTriggerOnChangeForDateTimeOutsideRange
                                }
                                onChangeDateTime={onChange}
                                className={classnames(theme.time, {
                                    [theme.noDate]: !showDate
                                })}
                                format={timeFormat}
                                closeOnBlur={closeOnBlur}
                                onFocus={onFocus}
                                visible={timeVisible}
                                controlVisibility={controlTimeVisibility}
                                ref={this.getTimePickerRef}
                                options={{
                                    format: {
                                        hour: timeFormat
                                    }
                                }}
                                placeholder={timePlaceholder}
                                portal={portal}
                            />
                        </div>
                        {isClearable && value && (
                            <ClearButton theme={theme} onClick={onClearInput} />
                        )}
                    </>
                ) : null}
                {error ? (
                    <ErrorIcon
                        theme={theme}
                        iconType={IconTypes.Error}
                        popOverContent={error}
                    />
                ) : null}
                {info ? (
                    <ErrorIcon
                        theme={theme}
                        iconType={IconTypes.Info}
                        popOverContent={info}
                    />
                ) : null}
                {children}
            </div>
        );
    }
}

const ThemedDateTimePicker = compose(
    themr('DateTimePicker', theme),
    translate()
)(DateTimePicker);
export { ThemedDateTimePicker as DateTimePicker };
export default ThemedDateTimePicker;
